import { PDFDocument, degrees } from "pdf-lib";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";

export type PageDescriptor = {
  url: string;
  pageIndex: number;
};

export type DocumentDescriptor = PageDescriptor;

export type PreviewDescriptor = PageDescriptor & {
  bytesLength: number;
};

/**
 * Helper function to determine whether the first page of a PDF is effectively blank.
 * @param pdfUrl URL or ArrayBuffer-like source for the PDF document
 * @param whiteThreshold Pixel value threshold (0-255) above which a channel is considered white
 * @param blankRatio Ratio of white pixels required to treat the page as blank (0-1)
 * @returns Promise that resolves to `true` when the page is blank, otherwise `false`
 */
export async function isPageBlank(
  pdfUrl: string,
  whiteThreshold = 245,
  blankRatio = 0.995,
): Promise<boolean> {
  let pdf: any = null;
  let canvas: HTMLCanvasElement | null = null;
  try {
    pdf = await pdfjsLib.getDocument(pdfUrl).promise;
    const page = await pdf.getPage(1);
    const viewport = page.getViewport({ scale: 0.25 });
    canvas = document.createElement("canvas");
    canvas.width = Math.round(viewport.width);
    canvas.height = Math.round(viewport.height);
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) {
      console.error("isPageBlank error: Unable to get 2D canvas context");
      return false;
    }
    await page.render({ canvasContext: ctx, viewport }).promise;
    const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    let white = 0;
    const total = canvas.width * canvas.height;

    for (let i = 0; i < data.length; i += 16) {
      if (
        data[i + 3] === 0 ||
        (data[i] >= whiteThreshold &&
          data[i + 1] >= whiteThreshold &&
          data[i + 2] >= whiteThreshold)
      )
        white++;
    }

    return white / (total / 4) >= blankRatio;
  } catch (err) {
    console.error("isPageBlank error:", err);
    return false;
  } finally {
    if (pdf) {
      await pdf.destroy();
    }

    if (canvas) {
      canvas.width = 0;
      canvas.height = 0;
    }
  }
}

/**
 * Create preview PDF blobs (one-page PDFs) from an ArrayBuffer of a multi-page PDF.
 * @param arrayBuffer The source PDF as an ArrayBuffer
 * @returns Promise resolving to an array of preview objects: `{ pageIndex, url, bytesLength }`
 * @note Consumers MUST call URL.revokeObjectURL(preview.url) on each preview when no longer needed
 *       to prevent memory leaks from accumulated Blob URLs in the browser heap.
 */
export async function createPreviewsFromArrayBuffer(
  arrayBuffer: ArrayBuffer,
): Promise<PreviewDescriptor[]> {
  try {
    const srcPdf = await PDFDocument.load(arrayBuffer);
    const totalPages = srcPdf.getPageCount();

    const previews: PreviewDescriptor[] = [];
    for (let i = 0; i < totalPages; i++) {
      const one = await PDFDocument.create();
      const [copied] = await one.copyPages(srcPdf, [i]);
      one.addPage(copied);
      const bytes = await one.save();
      const blob = new Blob([new Uint8Array(bytes)], {
        type: "application/pdf",
      });
      previews.push({
        pageIndex: i,
        url: URL.createObjectURL(blob),
        bytesLength: bytes.length,
      });
    }

    return previews;
  } catch (err) {
    console.error("createPreviewsFromArrayBuffer error:", err);
    return [];
  }
}

/**
 * Detect blank-page markers between documents. A marker is `true` when the following page is blank.
 * @param docs Array of document descriptors with `{ url, pageIndex }`
 * @param whiteThreshold White pixel threshold passed to `isPageBlank`
 * @param blankRatio Blank ratio passed to `isPageBlank`
 * @returns Promise resolving to an array of boolean markers with length `docs.length - 1`
 */
export async function detectBlankMarkers(
  docs: DocumentDescriptor[],
  whiteThreshold = 245,
  blankRatio = 0.995,
): Promise<boolean[]> {
  if (docs.length <= 1) return [];

  const markers = new Array(docs.length - 1).fill(false);

  const results = await Promise.all(
    docs.map(async (doc, index) => ({
      index,
      isBlank: await isPageBlank(doc.url, whiteThreshold, blankRatio),
    })),
  );

  results.forEach(({ index, isBlank }) => {
    if (isBlank && index > 0) markers[index - 1] = true;
  });

  return markers;
}

/**
 * Generate split PDF files based on markers and rotation information.
 * @param arrayBuffer Source PDF as ArrayBuffer
 * @param markers Boolean markers where `true` indicates a split after that page index
 * @param rotations Array of rotation degrees indexed by global page index
 * @param docs Array of document descriptors with `{ url, pageIndex }`
 * @param baseName Base file name to use for generated parts
 * @returns Promise resolving to an array of `File` objects representing split PDFs
 */
export async function generateSplitFiles(
  arrayBuffer: ArrayBuffer,
  markers: boolean[],
  rotations: number[],
  docs: DocumentDescriptor[],
  baseName: string,
): Promise<File[]> {
  try {
    const srcPdf = await PDFDocument.load(arrayBuffer);
    const totalPages = srcPdf.getPageCount();

    const ranges: Array<[number, number]> = [];
    let start = 0;
    markers.forEach((mark, i) => {
      if (mark) {
        ranges.push([start, i]);
        start = i + 1;
      }
    });
    ranges.push([start, totalPages - 1]);

    const remainingIndexes = new Set<number>();

    const validDocs = docs.filter((doc) => {
      const pi = doc.pageIndex;
      const inBounds = typeof pi === "number" && pi >= 0 && pi < totalPages;
      if (!inBounds) {
        console.warn(
          "generateSplitFiles: skipping out-of-bounds pageIndex",
          pi,
        );
      } else {
        remainingIndexes.add(pi);
      }
      return inBounds;
    });

    const blankResults = await Promise.all(
      validDocs.map(async (doc) => ({
        pageIndex: doc.pageIndex,
        isBlank: await isPageBlank(doc.url),
      })),
    );
    const blankSet = new Set<number>(
      blankResults.filter((r) => r.isBlank).map((r) => r.pageIndex),
    );

    const generated: File[] = [];
    for (let idx = 0; idx < ranges.length; idx++) {
      const [s, e] = ranges[idx];
      const pages = Array.from({ length: e - s + 1 }, (_, k) => s + k).filter(
        (p) => remainingIndexes.has(p) && !blankSet.has(p),
      );
      if (!pages.length) continue;

      const newPdf = await PDFDocument.create();
      const copied = await newPdf.copyPages(srcPdf, pages);
      copied.forEach((p, pIdx) => {
        const globalPageIdx = pages[pIdx];
        const deg = rotations[globalPageIdx] ?? 0;
        if (deg) {
          const currentRot = p.getRotation().angle;
          p.setRotation(degrees((currentRot + deg) % 360));
        }
        newPdf.addPage(p);
      });

      const bytes = await newPdf.save();
      generated.push(
        new File([new Uint8Array(bytes)], `${baseName}_part${idx + 1}.pdf`, {
          type: "application/pdf",
        }),
      );
    }

    return generated;
  } catch (err) {
    console.error("generateSplitFiles error:", err);
    return [];
  }
}

/**
 * Generate one PDF per page for every page described in `docs`.
 * @param arrayBuffer Source PDF as ArrayBuffer
 * @param rotations Array of rotation degrees indexed by global page index
 * @param docs Array of document descriptors with `{ url, pageIndex }`
 * @param baseName Base file name to use for generated parts
 * @returns Promise resolving to an array of `File` objects representing single-page PDFs
 */
export async function generatePerPageFiles(
  arrayBuffer: ArrayBuffer,
  rotations: number[],
  docs: DocumentDescriptor[],
  baseName: string,
): Promise<File[]> {
  try {
    const srcPdf = await PDFDocument.load(arrayBuffer);
    const totalPages = srcPdf.getPageCount();
    const generated: File[] = [];

    for (let i = 0; i < docs.length; i++) {
      const pageIndex = docs[i].pageIndex;
      if (
        typeof pageIndex !== "number" ||
        pageIndex < 0 ||
        pageIndex >= totalPages
      ) {
        console.warn(
          "generatePerPageFiles: skipping out-of-bounds pageIndex",
          pageIndex,
        );
        continue;
      }

      const newPdf = await PDFDocument.create();
      const [copied] = await newPdf.copyPages(srcPdf, [pageIndex]);

      const deg = rotations[pageIndex] ?? 0;
      if (deg) {
        const currentRot = copied.getRotation().angle;
        copied.setRotation(degrees((currentRot + deg) % 360));
      }

      newPdf.addPage(copied);
      const bytes = await newPdf.save();
      generated.push(
        new File(
          [new Uint8Array(bytes)],
          `${baseName}_page${pageIndex + 1}.pdf`,
          { type: "application/pdf" },
        ),
      );
    }

    return generated;
  } catch (err) {
    console.error("generatePerPageFiles error:", err);
    return [];
  }
}
